// src/components/providerAssignments/ReassignmentDialog.tsx
//
// Reassignment as a first-class flow (spec §9). Fields:
//   current provider (readonly)
//   replacement provider (required)
//   provider role
//   priority
//   effective date
//   reason
//   transfer responsibilities checkbox
//   transfer tasks checkbox
//
// The backend response includes `taskTransferDeferred: boolean`. When
// true, the success banner explicitly states that task transfer is not
// yet automated — we do NOT pretend tasks moved.

import { useEffect, useMemo, useState } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { useHasCapability } from '../../services/centralContext';
import {
  useGetProviderRolesQuery,
  useReassignProviderAssignmentMutation,
} from '../../api/providerAssignmentsApi';
import { useGetTeamMembersQuery } from '../../api/teamApi';
import { useActiveWorkspace } from '../../hooks/useActiveWorkspace';
import type {
  AssignmentPriority,
  PersonProviderAssignment,
  ReassignAssignmentResponse,
} from '../../types/api';
import { ASSIGNMENT_PRIORITIES } from '../../types/api';
import {
  PRIORITY_LABELS,
  providerLabel,
} from './assignmentDisplay';

export interface ReassignmentDialogProps {
  assignment: PersonProviderAssignment;
  onClose: () => void;
  onReassigned?: (res: ReassignAssignmentResponse) => void;
}

interface FormValues {
  newProviderUserId: string;
  newProviderRoleId: string;
  newPriority: AssignmentPriority;
  effectiveDate: string;
  reason: string;
  transferResponsibilities: boolean;
  transferOpenTasks: boolean;
}

export default function ReassignmentDialog({
  assignment,
  onClose,
  onReassigned,
}: ReassignmentDialogProps) {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;
  const canReassign = useHasCapability('assignments', 'reassign');

  const { data: teamMembers = [] } = useGetTeamMembersQuery(
    { workspaceId: workspaceId ?? '' },
    { skip: !workspaceId },
  );
  const { data: roles = [] } = useGetProviderRolesQuery();

  const [values, setValues] = useState<FormValues>({
    newProviderUserId: '',
    newProviderRoleId: assignment.providerRoleId,
    newPriority: assignment.priority,
    effectiveDate: '',
    reason: '',
    transferResponsibilities: false,
    transferOpenTasks: false,
  });
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [result, setResult] = useState<ReassignAssignmentResponse | null>(null);

  const [reassign, { isLoading }] = useReassignProviderAssignmentMutation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === values.newProviderRoleId),
    [roles, values.newProviderRoleId],
  );
  // If chosen role can't be primary, coerce.
  useEffect(() => {
    if (selectedRole && !selectedRole.canBePrimary && values.newPriority === 'primary') {
      setValues((v) => ({ ...v, newPriority: 'secondary' }));
    }
  }, [selectedRole, values.newPriority]);

  const submit = async () => {
    if (!values.newProviderUserId) return;
    setError(null);
    setResult(null);
    try {
      const res = await reassign({
        id: assignment.id,
        data: {
          newProviderUserId: values.newProviderUserId,
          newProviderRoleId: values.newProviderRoleId,
          newPriority: values.newPriority,
          effectiveDate: values.effectiveDate || null,
          reason: values.reason.trim() || null,
          transferResponsibilities: values.transferResponsibilities,
          transferOpenTasks: values.transferOpenTasks,
        },
      }).unwrap();
      setResult(res);
      onReassigned?.(res);
    } catch (err) {
      const e = err as { data?: { error?: string; code?: string }; status?: number };
      setError({
        code: e?.data?.code || `http_${e?.status ?? 'unknown'}`,
        message: e?.data?.error || 'Reassignment failed.',
      });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reassign-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
      data-testid="reassign-dialog"
    >
      <div className="bg-white rounded-xl shadow-xl ring-1 ring-slate-200 w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
          <h2 id="reassign-title" className="flex-1 text-sm font-semibold text-slate-900">
            Reassign provider
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
          {!canReassign ? (
            <p className="text-sm text-slate-600">
              You don&apos;t have permission to reassign provider assignments.
            </p>
          ) : (
            <>
              <div className="rounded-md bg-slate-50 p-3 text-xs">
                <div className="text-slate-500">Current provider</div>
                <div className="mt-0.5 font-medium text-slate-900">
                  {providerLabel(assignment.provider)}
                </div>
                <div className="mt-1 text-slate-500">
                  {assignment.departmentCircle?.name} · {PRIORITY_LABELS[assignment.priority]}
                </div>
              </div>

              <div>
                <label
                  htmlFor="reassign-provider"
                  className="block text-[11px] font-medium text-slate-700 mb-1"
                >
                  Replacement provider <span className="text-red-500">*</span>
                </label>
                <select
                  id="reassign-provider"
                  value={values.newProviderUserId}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, newProviderUserId: e.target.value }))
                  }
                  className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                >
                  <option value="">— select a provider —</option>
                  {teamMembers
                    .filter((m) => m.userId !== assignment.providerUserId)
                    .map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {providerLabel({ fullName: m.fullName, email: m.email })}
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-700 mb-1">
                    Provider role
                  </label>
                  <select
                    value={values.newProviderRoleId}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, newProviderRoleId: e.target.value }))
                    }
                    className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                  >
                    {roles
                      .filter((r) => r.isActive)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={values.newPriority}
                    onChange={(e) =>
                      setValues((v) => ({
                        ...v,
                        newPriority: e.target.value as AssignmentPriority,
                      }))
                    }
                    className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                  >
                    {ASSIGNMENT_PRIORITIES.map((p) => (
                      <option
                        key={p}
                        value={p}
                        disabled={p === 'primary' && !!selectedRole && !selectedRole.canBePrimary}
                      >
                        {PRIORITY_LABELS[p]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-700 mb-1">
                  Effective date <span className="font-normal text-slate-400">— defaults to today</span>
                </label>
                <input
                  type="date"
                  value={values.effectiveDate}
                  onChange={(e) => setValues((v) => ({ ...v, effectiveDate: e.target.value }))}
                  className="block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-700 mb-1">
                  Reason
                </label>
                <textarea
                  rows={2}
                  value={values.reason}
                  onChange={(e) => setValues((v) => ({ ...v, reason: e.target.value }))}
                  className="block w-full resize-none rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                  placeholder="Why is this provider being replaced?"
                />
              </div>

              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={values.transferResponsibilities}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, transferResponsibilities: e.target.checked }))
                    }
                  />
                  Copy responsibilities from the current assignment
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={values.transferOpenTasks}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, transferOpenTasks: e.target.checked }))
                    }
                  />
                  Transfer the current provider&apos;s open tasks/follow-ups
                </label>
              </div>

              {result && (
                <div
                  className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
                  role="status"
                >
                  <div className="flex items-start gap-2">
                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <div>
                      <p>Assignment was reassigned successfully.</p>
                      {result.taskTransferDeferred && (
                        <p className="mt-1 font-medium">
                          Task transfer is not yet automated — you&apos;ll need to move
                          open tasks manually.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div
                  className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800"
                  role="alert"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{error.message}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && canReassign && (
            <button
              type="button"
              onClick={submit}
              disabled={isLoading || !values.newProviderUserId}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              data-testid="reassign-submit"
            >
              {isLoading ? 'Reassigning…' : 'Reassign'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
